import { useMemo } from 'react';

import { useWorkspace } from '@/features/workspaces';
import { buildBoard, locateTask } from '@/features/tasks';

import { resolveRun } from '../runBoard';

/**
 * Derives a `RunResolution` for one parent task, the same way
 * `TaskDetailsPanel`/`BoardPage` derive their own board data — one
 * `useWorkspace` read, located and resolved, never a second fetch keyed on
 * the execution or the target workflow (research.md Decision 1).
 *
 * `includeHidden: true` always: every task this page needs to see is, by
 * construction, hidden on the target board (spec 109) — there is no toggle
 * here the way `BoardPage`'s `showHidden` is one, because there is nothing
 * to show *instead*.
 */
export function useLinkedRun(
  workspaceId: string,
  workflowKey: string,
  taskId: string,
  /** A specific run from the address (`?execution=`); absent means the latest. */
  executionId?: string,
) {
  const { data: workspace, isPending } = useWorkspace(workspaceId, {
    includeHidden: true,
  });

  const parentWorkflow = workspace?.workflows.find(
    (candidate) => candidate.key === workflowKey,
  );
  const parentTask = workspace?.tasks.find(
    (candidate) => candidate.id === taskId,
  );

  // `buildBoard` over the *parent's own* workflow, to locate the parent task's
  // current step and the run's own step — this is never the board the page renders
  // (that is `resolveRun`'s own `buildBoard` call, over the target
  // workflow and the filtered task set).
  const parentBoard = useMemo(() => {
    if (!parentWorkflow) return undefined;
    const built = buildBoard(parentWorkflow, workspace?.tasks ?? []);
    return built.ok ? built.board : undefined;
  }, [parentWorkflow, workspace?.tasks]);

  const location =
    parentBoard && parentTask
      ? locateTask(parentBoard, parentTask.id)
      : undefined;

  const resolution = useMemo(() => {
    if (!workspace || !parentTask) return undefined;
    return resolveRun({
      parentTask,
      allTasks: workspace.tasks,
      parentBoard,
      location,
      workflows: workspace.workflows,
      executionId,
    });
  }, [workspace, parentTask, location, parentBoard, executionId]);

  return { isPending, workspace, parentTask, resolution };
}
